import { useState, useEffect, useRef, createContext, useContext } from 'react'
import { supabase } from '../lib/supabase'
import { setOrgScheduling } from '../lib/businessDays'

const AuthContext = createContext(null)

const PROFILE_CACHE_KEY = 'profile_cache'

function readProfileCache(userId) {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY)
    if (!raw) return null
    const cached = JSON.parse(raw)
    return cached?.id === userId ? cached : null
  } catch { return null }
}

function writeProfileCache(data) {
  try { localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(data)) } catch {}
}

function clearProfileCache() {
  try { localStorage.removeItem(PROFILE_CACHE_KEY) } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null)
  const [profile, setProfile] = useState(null)
  // Start loading=false if there are no Supabase tokens in storage — login page
  // shows immediately with no delay instead of waiting for auth to resolve.
  const [loading, setLoading] = useState(() => {
    try {
      return Object.keys(localStorage).some(k => k.startsWith('sb-') && k.includes('-auth-token'))
    } catch { return true }
  })
  // Detect invite / password-recovery links on first load
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1))
    const type = hash.get('type')
    return type === 'invite' || type === 'recovery'
  })

  // Ref so auth callbacks can read the current profile without stale closures
  const profileRef = useRef(null)
  useEffect(() => { profileRef.current = profile }, [profile])

  const signingOutRef = useRef(false)

  const fetchProfile = async (userId) => {
    const { data } = await supabase
      .from('profiles')
      .select('*, orgs(features, settings, name)')
      .eq('id', userId)
      .maybeSingle()
    if (data) writeProfileCache(data)
    setProfile(data || null)
    setOrgScheduling(data?.orgs?.settings?.scheduling ?? null)
    return data ?? null
  }

  useEffect(() => {
    let cancelled = false

    // onAuthStateChange fires INITIAL_SESSION on mount (handling token refresh
    // internally), so getSession() is not needed and would race against SIGNED_IN
    // events if a stale refresh token is present in storage.
    const fallback = setTimeout(() => { if (!cancelled) setLoading(false) }, 1000)

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (cancelled) return
      clearTimeout(fallback)

      // If Supabase fires SIGNED_OUT while we have an active profile, a transient
      // DB timeout may have killed the token-refresh query rather than a real
      // sign-out. Attempt one getSession() recovery before clearing the session.
      // Skip this check when the user explicitly clicked sign out.
      if (!session && _event === 'SIGNED_OUT' && profileRef.current && !signingOutRef.current) {
        const { data: recovered } = await supabase.auth.getSession()
        if (recovered?.session?.user) return  // still valid — ignore the spurious event
      }
      signingOutRef.current = false

      setUser(session?.user ?? null)

      if (session?.user) {
        const alreadyLoaded = !!profileRef.current

        if (alreadyLoaded) {
          // Profile already in memory (tab focus / token refresh) — silent background
          // refresh with no loading screen or retry loop so the UI isn't disrupted.
          fetchProfile(session.user.id).catch(() => {})
          return
        }

        // Use cached profile immediately so the app renders without waiting for
        // a DB round-trip, then refresh in the background.
        const cached = readProfileCache(session.user.id)
        if (cached) {
          setProfile(cached)
          setOrgScheduling(cached?.orgs?.settings?.scheduling ?? null)
          if (!cancelled) setLoading(false)
          fetchProfile(session.user.id).catch(() => {})
          return
        }

        // No cache (first login or cleared) — fetch with retry
        if (_event === 'SIGNED_IN') setLoading(true)
        for (let i = 0; i < 2 && !cancelled; i++) {
          if (i > 0) await new Promise(r => setTimeout(r, 1000))
          const ok = await Promise.race([
            fetchProfile(session.user.id),
            new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 3000)),
          ]).catch(() => false)
          if (ok) break
        }
      } else {
        setProfile(null)
        setOrgScheduling(null)
        clearProfileCache()
      }
      if (!cancelled) setLoading(false)
    })

    return () => {
      cancelled = true
      clearTimeout(fallback)
      subscription.unsubscribe()
    }
  }, [])

  const refreshProfile  = () => { if (user) fetchProfile(user.id).catch(() => {}) }
  const signIn          = (email, password) => supabase.auth.signInWithPassword({ email, password })
  const signOut         = () => { signingOutRef.current = true; return supabase.auth.signOut() }
  const forgotPassword  = (email) => supabase.auth.resetPasswordForEmail(email)
  const isEditor           = profile?.role === 'admin'
  const isSubtaskEditor    = profile?.role === 'project_lead'
  const isLineService      = profile?.role === 'line_service'
  const isTechnician       = profile?.role === 'technician'
  const isInspector        = profile?.role === 'inspector'
  const canAccessEquipment = isEditor || isLineService
  const allowedModule      = profile?.allowed_module ?? null
  const isTrainingEditor   = isEditor || profile?.role === 'trainer'
  const isSuperAdmin       = profile?.is_super_admin ?? false
  const hasFeature         = (key) => (profile?.orgs?.features ?? []).includes(key)
  const mustChangePassword = profile?.must_change_password ?? false
  const completePasswordSetup = () => setNeedsPasswordSetup(false)
  const clearMustChangePassword = async () => {
    if (user) {
      await supabase.from('profiles').update({ must_change_password: false }).eq('id', user.id)
      fetchProfile(user.id).catch(() => {})
    }
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signOut, forgotPassword, refreshProfile, isEditor, isSubtaskEditor, isLineService, isTechnician, isInspector, canAccessEquipment, allowedModule, isTrainingEditor, isSuperAdmin, hasFeature, needsPasswordSetup, completePasswordSetup, mustChangePassword, clearMustChangePassword }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
